import axios from "axios";

/**
 * When REACT_APP_API_BASE_URL points at localhost but the SPA is opened by LAN hostname/IP
 * (e.g. Docker Compose + http://192.168.1.10:3000), the browser would call the wrong machine.
 * Rewrite loopback host to the page hostname so POST /admin/projects/ hits the same host as :8000.
 *
 * On a production public host with a misconfigured localhost API URL, prefer same-origin /api/v1
 * instead of rewriting to hostname:8000 (which is almost never correct behind nginx/TLS).
 */
function isPrivateOrLanHost(hostname) {
  if (!hostname) return false;
  if (hostname.endsWith(".local")) return true;
  return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname);
}

function alignApiBaseUrlWithPageHost(apiUrl) {
  if (typeof window === "undefined" || !apiUrl) return apiUrl;
  const pageHost = window.location.hostname;
  if (!pageHost || pageHost === "localhost" || pageHost === "127.0.0.1") {
    return apiUrl.replace(/\/$/, "");
  }
  try {
    const u = new URL(apiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
      const pagePort = String(window.location.port || "");
      const pageIsDevSpa = ["3000", "3001", "5173"].includes(pagePort);
      if (isPrivateOrLanHost(pageHost) || pageIsDevSpa) {
        u.hostname = pageHost;
        return u.toString().replace(/\/$/, "");
      }
      return `${window.location.origin.replace(/\/$/, "")}/api/v1`;
    }
  } catch {
    return apiUrl.replace(/\/$/, "");
  }
  return apiUrl.replace(/\/$/, "");
}

const resolveApiBaseUrl = () => {
  const raw = (process.env.REACT_APP_API_BASE_URL || "").trim();
  let base;
  if (raw) {
    base = raw.replace(/\/$/, "");
  } else if (typeof window !== "undefined") {
    const { protocol, hostname, port } = window.location;
    const safeHost = hostname === "127.0.0.1" ? "127.0.0.1" : hostname;
    // CRA / Vite dev servers: assume Django on same host, port 8000.
    const devPorts = new Set(["3000", "3001", "5173"]);
    if (devPorts.has(String(port))) {
      base = `${protocol}//${safeHost}:8000/api/v1`;
    } else {
      // Production-style: API under same origin (e.g. nginx /api/v1 → Django).
      base = `${window.location.origin.replace(/\/$/, "")}/api/v1`;
    }
  } else {
    base = "http://localhost:8000/api/v1";
  }
  return alignApiBaseUrlWithPageHost(base);
};

/** Default for normal API calls. Uploads / PDF exports override per-request. */
export const API_TIMEOUT_MS = 30000;
export const API_UPLOAD_TIMEOUT_MS = 120000;
export const API_REPORT_TIMEOUT_MS = 90000;

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: API_TIMEOUT_MS,
});

const AUTH_SCREEN_PATH_MARKERS = [
  "/auth/login/",
  "/auth/register/",
  "/auth/referral-lookup/",
  "/auth/verify-email/",
  "/auth/resend-email-otp/",
  "/auth/password/reset/",
  "/auth/password/reset/confirm/",
];

const isAuthScreenEndpointUrl = (requestUrl) =>
  AUTH_SCREEN_PATH_MARKERS.some((marker) => requestUrl.includes(marker));

const clearAuthAndRedirectToLogin = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const haltAfterAuthRedirect = (error) => {
  // Reject so callers' `finally` runs (e.g. clears submitting state) before navigation unloads the page.
  return Promise.reject(error || new Error("Authentication required."));
};

/** Single-flight refresh so parallel 401s do not burn a rotating refresh token. */
let refreshInFlight = null;

const refreshAccessToken = async () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    const refresh = localStorage.getItem("refreshToken");
    if (!refresh) {
      throw new Error("Missing refresh token");
    }
    const res = await axios.post(`${api.defaults.baseURL}/auth/token/refresh/`, { refresh });
    localStorage.setItem("accessToken", res.data.access);
    if (res.data.refresh) {
      localStorage.setItem("refreshToken", res.data.refresh);
    }
    return res.data.access;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

api.interceptors.request.use((config) => {
  const requestUrl = typeof config.url === "string" ? config.url : "";
  const isAuthScreenEndpoint = isAuthScreenEndpointUrl(requestUrl);
  const tokenRaw = localStorage.getItem("accessToken");
  const token = tokenRaw && !["undefined", "null", ""].includes(String(tokenRaw).trim()) ? tokenRaw : "";
  if (token && !isAuthScreenEndpoint) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (isAuthScreenEndpoint && config.headers?.Authorization) {
    delete config.headers.Authorization;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const isUnauthorized = error.response?.status === 401;
    const requestUrl = typeof original.url === "string" ? original.url : "";
    const isRefreshCall = requestUrl.includes("/auth/token/refresh/");
    const isAuthScreenEndpoint = isAuthScreenEndpointUrl(requestUrl);

    if (isUnauthorized && isAuthScreenEndpoint) {
      return Promise.reject(error);
    }

    if (isUnauthorized && !original._retry && !isRefreshCall) {
      original._retry = true;
      const refresh = localStorage.getItem("refreshToken");
      if (refresh) {
        try {
          const access = await refreshAccessToken();
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${access}`;
          return api(original);
        } catch (refreshError) {
          clearAuthAndRedirectToLogin();
          return haltAfterAuthRedirect(refreshError);
        }
      }
      clearAuthAndRedirectToLogin();
      return haltAfterAuthRedirect(error);
    }
    if (isUnauthorized && isRefreshCall) {
      clearAuthAndRedirectToLogin();
      return haltAfterAuthRedirect(error);
    }
    return Promise.reject(error);
  }
);

export default api;
